import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { registerModal, unregisterModal } from '@/redux/actions/uiActions/modalActions';
import { modalStackPosition } from '@/redux/selectors/uiSelectors';

/**
 * Custom hook for managing modal z-index stacking.
 *
 * Automatically registers/unregisters modals in Redux state and calculates
 * the appropriate z-index based on the modal's position in the stack.
 * This ensures nested modals always appear on top of their parent modals.
 *
 * @param {string} modalId - Unique identifier for the modal instance
 * @param {boolean} show - Whether the modal is currently visible
 * @returns {number} Calculated z-index value for the modal
 *
 * @example
 * const MyModal = ({ show, onHide }) => {
 *   const modalId = useRef(`modal-${randomId()}`);
 *   const zIndex = useModalStack(modalId.current, show);
 *
 *   return (
 *     <div style={{ zIndex }}>
 *       Modal content
 *     </div>
 *   );
 * };
 */
const useModalStack = (modalId, show) => {
  const dispatch = useDispatch();
  const stackPosition = useSelector((state) => modalStackPosition(state, modalId));

  useEffect(() => {
    if (show) {
      dispatch(registerModal(modalId));

      return () => {
        dispatch(unregisterModal(modalId));
      };
    }
  }, [show, modalId, dispatch]);

  const baseZIndex = 800;
  const zIndexIncrement = 10;
  const zIndex = stackPosition >= 0 ? baseZIndex + stackPosition * zIndexIncrement : baseZIndex;

  return zIndex;
};

export default useModalStack;
